import React, { useState, useEffect, useMemo } from "react";
import { colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { BackButton } from "../shared/BackButton";
import { useIsMobile } from "../shared/utils";
import { SlotCalendar } from "./SlotCalendar";
import { TabBar } from "../shared/TabBar";
import { SlotPicker } from "./SlotPicker";
import { TenantModal } from "./TenantModal";

const formatDate = (d) =>
  d
    ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
      })
    : "";

// What a tenancy can tell us about access. Three options, matching
// Booking.Choice on the server — distinct from the four statuses below, which
// are derived and include whether a time has actually been assigned.
const CHOICE_LABELS = {
  at_home: "At home",
  key_with_bm: "Key with BM",
  not_available: "Away",
};

// Four states, and only four: a unit is Booked once it has a time, needs one
// (Assign), or has told us it does not need one.
export function bookingStatus(row) {
  if (row.choice === "not_available") return "away";
  if (row.choice === "key_with_bm") return "key_with_bm";
  if (row.slot_id) return "booked";
  return "assign";
}

const STATUS_LABELS = {
  booked: "Booked",
  assign: "Assign",
  away: "Away",
  key_with_bm: "Key with BM",
};

// Assign leads: it is the work still outstanding.
const STATUS_ORDER = ["assign", "booked", "key_with_bm", "away"];

const STATUS_EMPTY = {
  assign: "Every unit has been dealt with — nothing left to assign.",
  booked: "No units have a time booked yet.",
  key_with_bm: "No units are leaving a key with the building manager.",
  away: "No units have said they'll be away.",
};

// Muted throughout: this is a status column, not a warning. "Assign" is the one
// that wants acting on, so it alone reads as a link.
const STATUS_COLORS = {
  booked: colors.green,
  assign: colors.activeBlue,
  away: colors.amber,
  key_with_bm: colors.slate,
};

// One board, two callers: the office through /portal/api/, and a building
// manager through /api/bm/. They are looking at the same campaign, so they get
// the same screen rather than two screens that resemble each other — which is
// the whole point, and why the differences between them are a handful of
// flags rather than a second implementation.
//
// Every default here is the staff screen exactly as it was, so a caller that
// passes only `client` and `campaignId` gets today's behaviour.
export function CampaignBoard({
  client,
  campaignId,
  onBack,
  backTitle = "Campaigns",
  // The office can clear a booking outright (DELETE /bookings/{id}/) and block
  // a time (PATCH /slots/{id}/). A manager has neither route on /api/bm/, so
  // these hide controls that would only ever fail.
  canClearBooking = true,
  canBlockSlots = true,
  // Tenants live on the asset register, which the two callers reach by
  // different endpoints — see portal_bm/BMTenantModal.js.
  TenantModalComponent = TenantModal,
  // The staff shell gives this screen the whole viewport; the manager portal
  // hands it what is left under its own header.
  fill = false,
}) {
  const isMobile = useIsMobile();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("units");
  const [statusTab, setStatusTab] = useState("all");
  // null = closed; {} = adding; a row = editing that tenancy.
  const [tenantEdit, setTenantEdit] = useState(null);

  // `background` refreshes the data without flipping `loading`. The content
  // area below is gated on `loading`, so a plain reload unmounts whatever is on
  // screen — for the Calendar tab that means losing the selected day and the
  // scroll position every time a single slot changes.
  const loadBoard = (id, { background = false } = {}) => {
    if (!id) return Promise.resolve();
    if (!background) setLoading(true);
    setError("");
    return client
      .get(`/campaigns/${id}/board/`)
      .then((r) => {
        setBoard(r.data);
        setLoading(false);
      })
      .catch(() => {
        setBoard(null);
        setError("Could not load the board for this campaign.");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadBoard(campaignId);
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const searched = useMemo(() => {
    const all = board?.rows || [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) =>
      `${r.unit_number} ${r.unit_name} ${r.tenant_name} ${r.tenant_mobile} ${r.tenant_email}`
        .toLowerCase()
        .includes(q)
    );
  }, [board, search]);

  // Counted after the search, so the tabs describe what you are actually
  // looking at rather than the whole building.
  const statusCounts = useMemo(() => {
    const c = { all: searched.length, assign: 0, booked: 0, key_with_bm: 0, away: 0 };
    searched.forEach((r) => (c[bookingStatus(r)] += 1));
    return c;
  }, [searched]);

  const rows = useMemo(
    () =>
      statusTab === "all"
        ? searched
        : searched.filter((r) => bookingStatus(r) === statusTab),
    [searched, statusTab]
  );

  const campaign = board?.campaign;
  // Six columns need the width of a desktop. On a phone the row stacks
  // instead — see BoardRow — and only the status stays on its own column.
  const cols = isMobile ? "1fr auto" : "110px 1.2fr 130px 1.4fr 120px 44px";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...(fill ? { flex: 1, minHeight: 0 } : { height: "100vh" }),
      }}
    >
      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <BackButton onClick={onBack} title={backTitle} />
          <div>
            <div style={styles.pageTitle}>Fire Inspection Bookings</div>
            {campaign && (
              <div style={{ fontSize: "13px", color: colors.slate, marginTop: "2px" }}>
                {campaign.name} · {campaign.site_name}
                {campaign.area_name && campaign.area_name !== campaign.name
                  ? ` · ${campaign.area_name}`
                  : ""}
              </div>
            )}
          </div>
        </div>
      </div>

      <TabBar
        tabs={["units", "calendar"]}
        tabLabels={{
          units: campaign?.area_name || "Units",
          calendar: "Calendar",
        }}
        activeTab={tab}
        onSelect={setTab}
      />

      {tab === "units" && (
        <div
          style={{
            padding: "10px 24px",
            backgroundColor: colors.white,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              style={{ ...inputStyle, flex: 1, width: "auto" }}
              placeholder="Search unit, name or mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {campaign?.area && (
              <button onClick={() => setTenantEdit({})} style={styles.addButton}>
                Add tenant
              </button>
            )}
          </div>
        </div>
      )}

      <div style={styles.content}>
        {loading ? (
          <div style={styles.card}>
            <div style={styles.loading}>Loading...</div>
          </div>
        ) : error ? (
          <div style={styles.card}>
            <div style={{ ...styles.loading, color: colors.red }}>{error}</div>
          </div>
        ) : !board ? (
          <div style={styles.card}>
            <div style={styles.loading}>Could not load this campaign.</div>
          </div>
        ) : board.unit_count === 0 ? (
          <div style={styles.card}>
            <div style={{ ...styles.loading, lineHeight: "1.6" }}>
              {campaign?.area_name || campaign?.site_name} has no tenants yet.
              {campaign?.area && (
                <div style={{ marginTop: "10px" }}>
                  <button onClick={() => setTenantEdit({})} style={styles.addButton}>
                    Add tenant
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : tab === "calendar" ? (
          <SlotCalendar
            client={client}
            campaignId={campaignId}
            boardRows={board.rows}
            canBlockSlots={canBlockSlots}
            onChanged={() => loadBoard(campaignId, { background: true })}
          />
        ) : (
        <div style={styles.card}>
          {/* Filters the list below it, so it sits inside the card rather than
              alongside the page-level Units/Calendar bar. */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              borderBottom: `1px solid ${colors.border}`,
              padding: "0 20px",
            }}
          >
            {["all", ...STATUS_ORDER].map((key) => {
              const active = statusTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setStatusTab(key)}
                  style={{
                    padding: "11px 14px",
                    fontSize: "13px",
                    fontWeight: "600",
                    border: "none",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    color: active ? colors.text : colors.slate,
                    borderBottom: `2px solid ${
                      active ? colors.activeBlue : "transparent"
                    }`,
                    marginBottom: "-1px",
                  }}
                >
                  {key === "all" ? "All" : STATUS_LABELS[key]} ({statusCounts[key]})
                </button>
              );
            })}
          </div>

          {rows.length === 0 ? (
            <div style={styles.loading}>
              {searched.length === 0
                ? "Nothing matches that search."
                : STATUS_EMPTY[statusTab]}
            </div>
          ) : (
            <>
              {!isMobile && (
                <div
                  style={{ ...styles.tableHeader, gridTemplateColumns: cols, gap: "12px" }}
                >
                  <div>Unit</div>
                  <div>Tenant</div>
                  <div>Mobile</div>
                  <div>Email</div>
                  <div>Booking</div>
                  <div />
                </div>
              )}
              {rows.map((r) => (
                <BoardRow
                  key={r.unit_id}
                  row={r}
                  cols={cols}
                  isMobile={isMobile}
                  onEdit={() => setEditing(r)}
                />
              ))}
              <div style={styles.count}>
                {rows.length} of {board.unit_count} unit
                {board.unit_count !== 1 ? "s" : ""}
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {tenantEdit && (
        <TenantModalComponent
          siteId={campaign?.site}
          areaId={campaign?.area}
          row={tenantEdit.unit_id ? tenantEdit : null}
          onClose={() => setTenantEdit(null)}
          onSaved={() => {
            setTenantEdit(null);
            setEditing(null);
            loadBoard(campaignId, { background: true });
          }}
        />
      )}

      {editing && (
        <EditBooking
          client={client}
          row={editing}
          campaignId={campaignId}
          canClearBooking={canClearBooking}
          isMobile={isMobile}
          onEditTenant={() => setTenantEdit(editing)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadBoard(campaignId);
          }}
        />
      )}
    </div>
  );
}

function BoardRow({ row, cols, isMobile, onEdit }) {
  const [hovered, setHovered] = useState(false);
  const status = bookingStatus(row);
  // Straight from the asset register — one source of truth for who is in a
  // tenancy, so an edit on either screen is an edit to the same record.
  const name = row.tenant_name || "";
  const mobile = row.tenant_mobile || "";
  const email = row.tenant_email || "";

  const statusCell = (
    <div
      style={{
        color: STATUS_COLORS[status],
        fontWeight: status === "assign" ? "500" : "600",
        whiteSpace: "nowrap",
      }}
      title={
        status === "booked"
          ? `${formatDate(row.slot_date)} ${row.slot_label}`
          : undefined
      }
    >
      {STATUS_LABELS[status]}
    </div>
  );

  const frame = {
    display: "grid",
    gridTemplateColumns: cols,
    gap: "12px",
    padding: isMobile ? "12px 16px" : "10px 20px",
    borderBottom: `1px solid ${colors.border}`,
    alignItems: "center",
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: hovered ? "#f8fafc" : colors.white,
  };

  // On a phone the six columns become one block plus the status: unit and
  // tenant on their own lines, mobile and email beneath. Same data, same
  // order, no horizontal scrolling.
  if (isMobile)
    return (
      <div
        onClick={onEdit}
        onTouchStart={() => setHovered(true)}
        onTouchEnd={() => setHovered(false)}
        style={frame}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: "600", color: colors.text }}>
            {row.unit_number}
            {row.unit_name ? ` · ${row.unit_name}` : ""}
          </div>
          <div
            style={{
              color: name ? colors.text : colors.slate,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name || "No contact on file"}
          </div>
          {(mobile || email) && (
            <div
              style={{
                fontSize: "12px",
                color: colors.slate,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {[mobile, email].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        {statusCell}
      </div>
    );

  return (
    <div
      onClick={onEdit}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={frame}
    >
      <div style={{ overflow: "hidden" }}>
        <div style={{ fontWeight: "600", color: colors.text }}>{row.unit_number}</div>
        {row.unit_name && (
          <div
            style={{
              fontSize: "12px",
              color: colors.slate,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.unit_name}
          </div>
        )}
      </div>
      <div
        style={{
          color: name ? colors.text : colors.slate,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name || "—"}
      </div>
      <div style={{ color: colors.slate, whiteSpace: "nowrap" }}>{mobile || "—"}</div>
      <div
        style={{
          color: colors.slate,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={email}
      >
        {email || "—"}
      </div>
      {statusCell}
      <div
        style={{
          textAlign: "right",
          color: colors.slate,
          fontSize: "12px",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.15s",
        }}
      >
        Edit
      </div>
    </div>
  );
}

// Everything on the board is editable by hand — a tenant rings up, you change
// it here rather than waiting for them to use the portal.
function EditBooking({
  client,
  row,
  campaignId,
  canClearBooking,
  isMobile,
  onClose,
  onSaved,
  onEditTenant,
}) {
  const [choice, setChoice] = useState(row.choice || "at_home");
  // Prefilled from the unit's contact when there is no booking yet, so taking
  // a phone call does not mean retyping details already held against the site.
  const [slotId, setSlotId] = useState(row.slot_id || "");
  const [reason, setReason] = useState(row.unavailable_reason || "");
  const [availableFrom, setAvailableFrom] = useState(row.available_from || "");
  const [notes, setNotes] = useState(row.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    const payload = {
      campaign: campaignId,
      unit: row.unit_id,
      choice,
      slot: choice === "at_home" && slotId ? Number(slotId) : null,
      unavailable_reason: choice === "not_available" ? reason : "",
      available_from: choice === "not_available" && availableFrom ? availableFrom : null,
      notes,
    };
    try {
      if (row.booking_id) {
        await client.patch(`/bookings/${row.booking_id}/`, payload);
      } else {
        await client.post("/bookings/", payload);
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save. Please try again.");
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!row.booking_id) return onClose();
    if (!window.confirm(`Clear the booking for unit ${row.unit_number}?`)) return;
    try {
      await client.delete(`/bookings/${row.booking_id}/`);
      onSaved();
    } catch (e) {
      setError("Could not clear the booking. Please try again.");
    }
  };

  const field = { marginBottom: "14px" };
  const label = {
    fontSize: "11px",
    fontWeight: "700",
    color: colors.slate,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "4px",
    display: "block",
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.15)", zIndex: 99 }}
      />
      <div
        style={{
          position: "fixed",
          zIndex: 100,
          backgroundColor: colors.white,
          display: "flex",
          flexDirection: "column",
          ...(isMobile
            ? {
                left: 0,
                right: 0,
                bottom: 0,
                maxHeight: "88vh",
                borderRadius: "16px 16px 0 0",
              }
            : {
                right: 0,
                top: 0,
                bottom: 0,
                width: "420px",
                boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
              }),
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: "700", fontSize: "18px", color: colors.text }}>
              Unit {row.unit_number}
            </div>
            <div style={{ fontSize: "12px", color: colors.slate }}>{row.floor_level}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: colors.slate,
              fontSize: "18px",
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <div style={field}>
            <label style={label}>Access</label>
            <div style={{ display: "flex", gap: "6px" }}>
              {Object.entries(CHOICE_LABELS).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setChoice(k)}
                  style={{
                    flex: 1,
                    padding: "8px 4px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                    border: `1px solid ${choice === k ? colors.darkGrey : colors.border}`,
                    backgroundColor: choice === k ? colors.darkGrey : colors.white,
                    color: choice === k ? colors.white : colors.slate,
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {choice === "at_home" && (
            <div style={field}>
              <label style={label}>Time slot</label>
              <SlotPicker
                client={client}
                campaignId={campaignId}
                value={slotId}
                initialDate={row.slot_date}
                currentBookingId={row.booking_id}
                onChange={setSlotId}
              />
            </div>
          )}

          {choice === "not_available" && (
            <>
              <div style={field}>
                <label style={label}>Reason</label>
                <input
                  style={inputStyle}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
              <div style={field}>
                <label style={label}>Next available from</label>
                <input
                  type="date"
                  style={inputStyle}
                  value={availableFrom}
                  onChange={(e) => setAvailableFrom(e.target.value)}
                />
              </div>
            </>
          )}

          {/* The tenant lives on the asset register, not on the booking — a
              second copy here could not stay in step with it. */}
          <div style={field}>
            <label style={label}>Tenant</label>
            <div style={{ fontSize: "14px", color: colors.text }}>
              {row.tenant_name || "No contact on file"}
            </div>
            {(row.tenant_mobile || row.tenant_email) && (
              <div style={{ fontSize: "13px", color: colors.slate, marginTop: "2px" }}>
                {[row.tenant_mobile, row.tenant_email].filter(Boolean).join(" · ")}
              </div>
            )}
            <button
              onClick={onEditTenant}
              style={{
                background: "none",
                border: "none",
                padding: "6px 0 0",
                cursor: "pointer",
                color: colors.activeBlue,
                fontSize: "13px",
              }}
            >
              Edit tenant on the register
            </button>
          </div>
          <div style={field}>
            <label style={label}>Notes</label>
            <textarea
              style={{ ...inputStyle, minHeight: "70px", resize: "vertical" }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && <div style={{ color: colors.red, fontSize: "13px" }}>{error}</div>}
        </div>

        <div
          style={{
            padding: "16px 24px",
            borderTop: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={canClearBooking ? remove : onClose}
            style={{
              background: "none",
              border: `1px solid ${colors.border}`,
              borderRadius: "6px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "13px",
              color: colors.slate,
            }}
          >
            {canClearBooking && row.booking_id ? "Clear booking" : "Cancel"}
          </button>
          <button onClick={save} disabled={saving} style={styles.addButton}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}
